import { t } from '@/lib/i18n'

export type StripDay = {
  /** YYYY-MM-DD */
  date: string
  /** 星期几的短名，今天那格换成「今天」 */
  label: string
  /** 日号 */
  day: number
  /** 那天完成了几项，0 表示圆点熄灭 */
  count: number
  /**
   * 录了但一件没做完 —— 圆点是琥珀色的半成品态（稿子的 `.dot.part`）。
   *
   * 不给就只有「有 / 没有」两态，打卡页就是这样（固定习惯总是存在的，
   * 「没录」不是一个状态）。作业需要第三态：每天一张新单子，
   * 「录了 8 件一件没做」和「压根没录」是两件完全不同的事。
   */
  partial?: boolean
}

/**
 * 稿子的 `.datestrip`：七天均分，**不横滑**。
 *
 * 取代原来挂在卡片右上角的 `<input type="date">`：圆点是这一条真正的价值 ——
 * 「哪天漏了」一眼看得出，而 date input 要点开日历一天天翻。
 *
 * `flex-1 basis-0 min-w-0` 三件套是关键：只给 flex-1 时，日号是两位数的格子会比
 * 一位数的宽，七格加起来在 320px 上溢出。basis-0 让宽度只由 flex 分配决定。
 *
 * sticky top-13：贴在 52px 的顶栏下沿。z-[29] 在顶栏（30）之下 —— 滚动时它该被
 * 顶栏盖住一像素，反过来就是一条线压在顶栏边框上。
 */
export default function DateStrip({ days, selected, onSelect }: {
  days: StripDay[]
  selected: string
  onSelect: (date: string) => void
}) {
  return (
    <div
      role="group"
      aria-label={t('checkin.strip_label')}
      className="sticky top-13 z-[29] flex gap-1.5 px-3 py-2.5 bg-card border-b border-line"
    >
      {days.map((d) => {
        const on = d.date === selected
        return (
          <button
            key={d.date}
            type="button"
            onClick={() => onSelect(d.date)}
            aria-pressed={on}
            aria-label={
              d.count > 0
                ? t('checkin.strip_day_done', { date: d.date, count: d.count })
                : t('checkin.strip_day_empty', { date: d.date })
            }
            className={
              'flex-1 basis-0 min-w-0 min-h-[58px] rounded-ctl flex flex-col items-center justify-center gap-[3px] border-[1.5px] ' +
              (on ? 'bg-brand text-white border-brand' : 'bg-sunk text-muted border-transparent')
            }
          >
            <span className="text-[11px]">{d.label}</span>
            <span className="text-[17px] font-bold leading-none">{d.day}</span>
            {/* 圆点在选中格上用 bg-current 而不是写死的白底：这一格本来就是
                text-white（白字压 --color-brand，浅深同值），圆点跟着它就对了。
                这样既不新造一个「白」的第二种写法，也过得了那条「不许有不透明白底」
                的守卫（它是**字面**扫描，注释里写出那个类名一样会红）。 */}
            <span
              className={
                'w-[5px] h-[5px] rounded-full ' +
                (d.count > 0
                  ? (on ? 'bg-current' : 'bg-green')
                  : d.partial
                    ? (on ? 'bg-current opacity-75' : 'bg-amber')
                    : (on ? 'bg-current opacity-45' : 'bg-transparent'))
              }
            />
          </button>
        )
      })}
    </div>
  )
}
